import { GameRecordsBase } from '../../shared/models/game-records-base';
import type { GameRecordOptions } from '../../shared/models/game-record-base';
import { GameRecordBase } from '../../shared/models/game-record-base';
import { NflGameRecord } from './game-record';
import type { IGame } from '../../shared/interfaces/IGame';
import type { IGameRecord } from '../../shared/interfaces/IGameRecord';
import { Enumerable } from '../../shared/linq';

/**
 * NFL-specific GameRecords.
 * - Takes a KnockoutObservable<string> for teamName (reactive — custom leagues can rename teams).
 * - Deduplicates by gameScheduleNum in initSubscribers.
 * - WinningTeamsRecord and LosingTeamsRecord use the observable teamName reactively.
 */
export class NflGameRecords extends GameRecordsBase {
  teamNameObs: KnockoutObservable<string>;

  protected override get recordOptions(): Partial<GameRecordOptions> {
    return NflGameRecord.NFL_OPTIONS;
  }

  constructor(
    Wins: KnockoutObservableArray<IGame>,
    Losses: KnockoutObservableArray<IGame>,
    Ties: KnockoutObservableArray<IGame>,
    teamName: KnockoutObservable<string>,
    fakeObs: KnockoutObservable<number | undefined>
  ) {
    // Pass the current value of the observable as the static teamName to the base.
    // We store the observable separately for reactive use.
    super(Wins, Losses, Ties, teamName(), fakeObs);

    this.teamNameObs = teamName;

    // Override WinningTeamsRecord and LosingTeamsRecord to use reactive teamNameObs.
    // The base computed is already created in super() but uses the static teamName.
    // Re-assign here so these computeds track teamNameObs reactively.
    this.WinningTeamsRecord = ko.computed((): IGameRecord => {
      const record = new GameRecordBase(fakeObs, this.recordOptions);
      const currentTeamName = this.teamNameObs();
      ko.utils.arrayForEach(Wins(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) > 0.5) {
          record.Wins.increment();
        }
      });
      ko.utils.arrayForEach(Losses(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) > 0.5) {
          record.Losses.increment();
        }
      });
      ko.utils.arrayForEach(Ties(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) > 0.5) {
          record.Ties.increment();
        }
      });
      return record;
    });

    this.LosingTeamsRecord = ko.computed((): IGameRecord => {
      const record = new GameRecordBase(fakeObs, this.recordOptions);
      const currentTeamName = this.teamNameObs();
      ko.utils.arrayForEach(Wins(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) <= 0.5) {
          record.Wins.increment();
        }
      });
      ko.utils.arrayForEach(Losses(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) <= 0.5) {
          record.Losses.increment();
        }
      });
      ko.utils.arrayForEach(Ties(), (item: IGame) => {
        if (this.getOppPct(currentTeamName, item) <= 0.5) {
          record.Ties.increment();
        }
      });
      return record;
    });
  }

  /**
   * NFL-specific initSubscribers.
   * Deduplicates by gameScheduleNum (not gameGuid like NBA/NHL/MLB).
   * Also keeps teamName-based home/away comparisons consistent with the observable.
   */
  override initSubscribers(): void {
    const self = this;

    // game.home/away may be KO observable (function) or plain object depending on the data path
    const unwrap = (val: any) => typeof val === 'function' ? val() : val;

    self.subscribers.push(
      self.Wins.subscribe((arr) => {
        self.fakeObs();
        self.resetWins();
        self.AllRecord.Wins(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Wins.increment();
          }
          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Wins.increment();
          }
          if (home.TeamName === self.teamNameObs()) {
            self.HomeRecord.Wins.increment();
          }
          if (away.TeamName === self.teamNameObs()) {
            self.AwayRecord.Wins.increment();
          }
        });
      })
    );

    self.subscribers.push(
      self.Losses.subscribe((arr) => {
        self.fakeObs();
        self.resetLosses();
        self.AllRecord.Losses(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Losses.increment();
          }
          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Losses.increment();
          }
          if (home.TeamName === self.teamNameObs()) {
            self.HomeRecord.Losses.increment();
          }
          if (away.TeamName === self.teamNameObs()) {
            self.AwayRecord.Losses.increment();
          }
        });
      })
    );

    self.subscribers.push(
      self.Ties.subscribe((arr) => {
        self.fakeObs();
        self.resetTies();
        self.AllRecord.Ties(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Ties.increment();
          }
          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Ties.increment();
          }
          if (home.TeamName === self.teamNameObs()) {
            self.HomeRecord.Ties.increment();
          }
          if (away.TeamName === self.teamNameObs()) {
            self.AwayRecord.Ties.increment();
          }
        });
      })
    );

    self.Wins.valueHasMutated();
    self.Losses.valueHasMutated();
    self.Ties.valueHasMutated();
  }
}
